import { create } from 'zustand'
import { languageProfileApi } from '@/api/placement'

/**
 * Нужен ли ученику мастер онбординга.
 *
 * `unknown` — ещё не спрашивали, `checking` — спрашиваем. Разделены намеренно:
 * пока ответа нет, редиректить нельзя, иначе учитель на секунду видел бы чужой
 * экран, а ученик — мигание страницы при каждой навигации.
 */
type OnboardingStatus = 'unknown' | 'checking' | 'required' | 'not_required'

interface OnboardingState {
  status: OnboardingStatus
  /** Для кого спрашивали: смена аккаунта обнуляет ответ. */
  userId: number | null
  /**
   * Номер поколения запроса.
   *
   * Ответ применяется, только если с момента его отправки не случилось ни
   * skip, ни clear, ни reset. Без этого «Продолжить позже», нажатое до ответа,
   * тут же отменялось прилетевшим `required` — и человека возвращало в мастер,
   * из которого он только что вышел.
   */
  generation: number
  ensureChecked: (userId: number) => Promise<void>
  /** Отказ от мастера: серверная отметка плюс мгновенное снятие гейта. */
  skip: (userId: number) => Promise<void>
  /** Мастер пройден или больше не нужен — гейт снять без запроса. */
  clear: () => void
  reset: () => void
}

export const useOnboardingStore = create<OnboardingState>()((set, get) => ({
  status: 'unknown',
  userId: null,
  generation: 0,

  ensureChecked: async (userId) => {
    const { status, userId: askedFor } = get()

    if (askedFor === userId && status !== 'unknown') {
      return
    }

    const generation = get().generation + 1

    set({ status: 'checking', userId, generation })

    try {
      const profile = await languageProfileApi.show(userId)

      // Ответ проверяется на актуальность: пока запрос летел, пользователь мог
      // выйти, войти под другим аккаунтом или отказаться от мастера.
      if (get().generation !== generation) {
        return
      }

      set({ status: profile.needs_onboarding ? 'required' : 'not_required' })
    } catch {
      // Гейт — не то место, где стоит спорить с сетью: не ответил профиль —
      // пускаем в кабинет. Онбординг важен, но не важнее доступа к платформе.
      if (get().generation === generation) {
        set({ status: 'not_required' })
      }
    }
  },

  skip: async (userId) => {
    set({ status: 'not_required', userId, generation: get().generation + 1 })

    // Ошибка не глотается: гейт снят в памяти, но после перезагрузки сервер
    // вернёт прежний ответ, и человек снова окажется в мастере. Он должен
    // узнать об этом сразу, а не через сутки.
    await languageProfileApi.skipOnboarding(userId)
  },

  clear: () => set((state) => ({ status: 'not_required', generation: state.generation + 1 })),

  reset: () => set((state) => ({ status: 'unknown', userId: null, generation: state.generation + 1 })),
}))
